export const REFERRAL_QUERY_PARAM = 'ref';
export const REFERRAL_ENTRY_QUERY_PARAM = 'referral_entry';
export const REFERRAL_ATTRIBUTION_STORAGE_KEY = 'bustly.referral.attribution';
export const REFERRAL_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;
export const REFERRAL_ATTRIBUTION_CLOCK_SKEW_MS = 5 * 60 * 1000;

export interface ReferralAttribution {
    code: string;
    entryId?: string | null;
    attributionToken?: string | null;
    inviterDisplayName?: string | null;
    acceptedAt: string;
}

const normalize = (value?: string | null): string => (value || '').trim();

export const readReferralCode = (search: string = ''): string => {
    const params = new URLSearchParams(search);
    return normalize(params.get(REFERRAL_QUERY_PARAM));
};

// Navigation state only. Referral eligibility and billing discounts still rely on
// the signed attribution token issued by the backend.
export const readReferralEntryId = (search: string = ''): string => {
    const entryId = normalize(new URLSearchParams(search).get(REFERRAL_ENTRY_QUERY_PARAM));
    return /^[A-Za-z0-9_-]{8,128}$/.test(entryId) ? entryId : '';
};

export const parseReferralAttribution = (value: string | null | undefined): ReferralAttribution | null => {
    if (!value) return null;
    try {
        const parsed = JSON.parse(value) as Partial<ReferralAttribution>;
        const code = normalize(parsed.code);
        if (!code || !parsed.acceptedAt) return null;
        const acceptedAtMs = Date.parse(parsed.acceptedAt);
        const ageMs = Date.now() - acceptedAtMs;
        if (!Number.isFinite(acceptedAtMs)
            || ageMs > REFERRAL_ATTRIBUTION_TTL_MS
            || ageMs < -REFERRAL_ATTRIBUTION_CLOCK_SKEW_MS) return null;
        return {
            code,
            entryId: normalize(parsed.entryId) || null,
            attributionToken: normalize(parsed.attributionToken) || null,
            inviterDisplayName: normalize(parsed.inviterDisplayName) || null,
            acceptedAt: parsed.acceptedAt,
        };
    } catch {
        return null;
    }
};

const storageKey = (entryId?: string | null) => entryId ? `${REFERRAL_ATTRIBUTION_STORAGE_KEY}:${entryId}` : REFERRAL_ATTRIBUTION_STORAGE_KEY;

export const readStoredReferralAttribution = (storage?: Pick<Storage, 'getItem'>, entryId?: string | null): ReferralAttribution | null => {
    if (!storage) return null;
    return parseReferralAttribution(storage.getItem(storageKey(entryId)));
};

export const saveReferralAttribution = (
    attribution: Omit<ReferralAttribution, 'acceptedAt'> & { acceptedAt?: string },
    storage?: Pick<Storage, 'setItem'>,
): ReferralAttribution | null => {
    const code = normalize(attribution.code);
    if (!code || !storage) return null;
    const next: ReferralAttribution = {
        code,
        entryId: normalize(attribution.entryId) || null,
        attributionToken: normalize(attribution.attributionToken) || null,
        inviterDisplayName: normalize(attribution.inviterDisplayName) || null,
        acceptedAt: attribution.acceptedAt || new Date().toISOString(),
    };
    storage.setItem(storageKey(next.entryId), JSON.stringify(next));
    return next;
};

export const clearStoredReferralAttribution = (storage?: Pick<Storage, 'removeItem'>, entryId?: string | null): void => {
    storage?.removeItem(storageKey(entryId));
};
